import formStyles from '../formStyles.module.scss';
import styles from './textAreaInput.module.scss';
/**
 * Textarea input field with a label for multi-line text input.
 *
 * @remarks
 * This is a controlled input. The value is managed via the `value` prop and updated with `onChange`.
 * The `onChange` callback provides the current input value and the native change event for flexibility. You can then use the value to update your form state as needed or run any other function onchange.
 * Additional native `<textarea>` props (e.g. `rows`, `maxLength`, `placeholder`) can be passed via `...rest`.
 *
 * If you provide `helpText` or `errorMessage`, they are programmatically associated via `aria-describedby`.
 * Providing an `errorMessage` marks the field invalid (`aria-invalid`) and renders the error text.
 *
 * @param props.id - Unique textarea ID used to associate the label
 * @param props.name - Field name used for form submission/validation
 * @param props.title - Visible label text
 * @param props.placeholder Optional - Placeholder text for the textarea
 * @param props.maxLength Optional - Maximum character length allowed
 * @param props.value - Current value of the textarea
 * @param props.onChange Optional - (value, e?) => {someFunc(value)}. Value is the current input value, e is the native change event if you need it. SomeFunc is the function you want to call with the new value.
 * @param props.onBlur Optional - (value, e?) => {someFunc(value)}. Value is the current input value, e is the native blur event if you need it. SomeFunc is the function you want to call with the new value on blur.
 * @param props.required Optional - Marks the field required (default: false)
 * @param props.showRequired Optional - Controls whether the required asterisk (*) is shown
 * @param props.helpText Optional - Helper text shown under the label
 * @param props.errorMessage Optional - Error message shown under the textarea
 * @param props.disabled Optional - Disables the textarea
 * @param props.formInstructionsId Optional - ID for additional accessibility description
 * @param props.rest - Any additional native `<textarea>` attributes
 *
 * @example
 * ```tsx
 * <TextAreaInput
 *   id="message"
 *   name="message"
 *   title="Message"
 *   value={form.message}
 *   onChange={(messageValue) => setForm({ ...form, message: messageValue })}
 *   rows={5}
 * />
 * ```
 */
const TextAreaInput = ({
    id,
    name,
    title,
    placeholder = '',
    maxLength = 5000,
    value,
    onChange,
    onBlur,
    required = false,
    showRequired = true,
    helpText,
    errorMessage,
    disabled,
    formInstructionsId,
    ...rest
}: {
    id: string;
    name: string;
    title: string;
    placeholder?: string;
    maxLength?: number;
    value: string;
    onChange?: (
        value: string,
        e?: React.ChangeEvent<HTMLTextAreaElement>,
    ) => void;
    onBlur?: (value: string, e?: React.FocusEvent<HTMLTextAreaElement>) => void;
    required?: boolean;
    showRequired?: boolean;
    helpText?: string;
    errorMessage?: string;
    disabled?: boolean;
    formInstructionsId?: string;
} & Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    'onChange' | 'onBlur' | 'value'
>) => {
    const helpId = `${id}-help`;
    const errorId = `${id}-error`;
    const asterisk = (
        <span
            aria-hidden={!showRequired}
            className={formStyles.requiredAsterisk}
        >
            {' '}
            *
        </span>
    );
    const describedBy = [
        helpText ? helpId : null,
        errorMessage ? errorId : null,
        formInstructionsId || null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            data-testid="textareainputinput"
            className={formStyles.inputcontainer}
        >
            <label
                data-testid="textareainputlabel"
                htmlFor={id}
                className={`${formStyles.label} ${formStyles[id]}`}
            >
                {title}
                {required && showRequired ? asterisk : ''}
            </label>

            {helpText && (
                <p id={helpId} className={formStyles.help}>
                    {helpText}
                </p>
            )}

            <textarea
                data-testid="textareainputinput"
                id={id}
                name={name}
                placeholder={placeholder}
                maxLength={maxLength}
                value={value}
                onChange={(e) => onChange?.(e.target.value, e)}
                onBlur={(e) => onBlur?.(e.target.value, e)}
                required={required}
                disabled={disabled}
                aria-invalid={Boolean(errorMessage) || undefined}
                aria-describedby={describedBy || undefined}
                aria-required={required || undefined}
                className={`${formStyles.input} ${rest.className ?? ''} ${styles.textAreaInput}`}
                {...rest}
            />

            {errorMessage && (
                <p id={errorId} className={formStyles.error} role="alert">
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default TextAreaInput;
